/**
 * Centralized file validation utility for secure file uploads
 * Prevents malicious file uploads and ensures consistent validation across the application
 */

/** Matches backend `AllowedMediaImageMime`. */
export enum AllowedImageMime {
  JPEG = 'image/jpeg',
  PNG = 'image/png',
  WEBP = 'image/webp'
}

export enum AllowedImageExtension {
  JPG = '.jpg',
  JPEG = '.jpeg',
  PNG = '.png',
  WEBP = '.webp'
}

export enum AllowedVideoMime {
  WEBM = 'video/webm'
}

export enum AllowedVideoExtension {
  WEBM = '.webm'
}

const ALLOWED_IMAGE_MIMES = Object.values(AllowedImageMime)
const ALLOWED_IMAGE_EXTENSIONS = Object.values(AllowedImageExtension)
const ALLOWED_IMAGE_NAME_PATTERN = /\.(jpe?g|png|webp)$/i

const EXTENSION_TO_MIME: Record<AllowedImageExtension, AllowedImageMime[]> = {
  [AllowedImageExtension.JPG]: [AllowedImageMime.JPEG],
  [AllowedImageExtension.JPEG]: [AllowedImageMime.JPEG],
  [AllowedImageExtension.PNG]: [AllowedImageMime.PNG],
  [AllowedImageExtension.WEBP]: [AllowedImageMime.WEBP]
}

export interface FileValidationOptions {
  allowedTypes?: string[]
  allowedExtensions?: string[]
  maxSizeBytes?: number
  minSizeBytes?: number
  allowExecutables?: boolean
}

export interface FileValidationResult {
  isValid: boolean
  error?: string
  warnings?: string[]
}

const DEFAULT_OPTIONS: Required<FileValidationOptions> = {
  allowedTypes: ALLOWED_IMAGE_MIMES,
  allowedExtensions: ALLOWED_IMAGE_EXTENSIONS,
  maxSizeBytes: 5 * 1024 * 1024, // 5MB
  minSizeBytes: 1024, // 1KB
  allowExecutables: false
}

// Dangerous file extensions that should never be allowed
const DANGEROUS_EXTENSIONS = [
  '.exe',
  '.bat',
  '.cmd',
  '.com',
  '.scr',
  '.pif',
  '.msi',
  '.jar',
  '.js',
  '.jse',
  '.vbs',
  '.vbe',
  '.ws',
  '.wsf',
  '.wsc',
  '.wsh',
  '.ps1',
  '.psm1',
  '.psd1',
  '.ps1xml',
  '.pssc',
  '.psc1',
  '.app',
  '.deb',
  '.pkg',
  '.dmg',
  '.rpm',
  '.sh',
  '.bash',
  '.zsh',
  '.fish',
  '.csh',
  '.tcsh',
  '.py',
  '.pl',
  '.rb',
  '.php',
  '.asp',
  '.aspx',
  '.jsp',
  '.zip',
  '.rar',
  '.7z',
  '.tar',
  '.gz',
  '.bz2',
  '.xz'
]

// MIME types that could be dangerous
const DANGEROUS_MIME_TYPES = [
  'application/x-executable',
  'application/x-msdownload',
  'application/x-msdos-program',
  'application/x-msi',
  'application/x-winexe',
  'application/x-javascript',
  'text/javascript',
  'application/javascript',
  'application/zip',
  'application/x-zip-compressed',
  'application/x-rar-compressed',
  'application/x-7z-compressed'
]

/**
 * Validates file type against MIME type and extension.
 * Mirrors backend `validateMediaFile`: allowed extension OR allowed MIME (whichever matches).
 */
const validateFileType = (file: File, options: Required<FileValidationOptions>): FileValidationResult => {
  const fileName = file.name.toLowerCase()
  const lastDotIndex = fileName.lastIndexOf('.')
  const fileExtension = lastDotIndex >= 0 ? fileName.substring(lastDotIndex) : ''
  const mimeType = (file.type || '').toLowerCase()

  // Check for dangerous extensions
  if (fileExtension && DANGEROUS_EXTENSIONS.includes(fileExtension)) {
    return {
      isValid: false,
      error: `File type '${fileExtension}' is not allowed for security reasons`
    }
  }

  // Check for dangerous MIME types
  if (mimeType && DANGEROUS_MIME_TYPES.includes(mimeType)) {
    return {
      isValid: false,
      error: `File type '${mimeType}' is not allowed for security reasons`
    }
  }

  const allowVideo = options.allowedTypes.includes(AllowedVideoMime.WEBM)
  const hasImageExt = fileExtension !== '' && ALLOWED_IMAGE_EXTENSIONS.includes(fileExtension as AllowedImageExtension)
  const hasVideoExt = allowVideo && fileExtension === AllowedVideoExtension.WEBM
  const hasImageMime = mimeType !== '' && mimeType.startsWith('image/') && options.allowedTypes.includes(mimeType)
  const hasVideoMime =
    allowVideo && mimeType !== '' && mimeType.startsWith('video/') && options.allowedTypes.includes(mimeType)

  const isImage = hasImageExt || hasImageMime
  const isVideo = hasVideoExt || hasVideoMime

  if (!isImage && !isVideo) {
    const allowedLabel = allowVideo ? 'jpg, jpeg, png, webp, webm' : 'jpg, jpeg, png, webp'

    return {
      isValid: false,
      error: `Unsupported file type. Allowed: ${allowedLabel}`
    }
  }

  // Same guard as backend strictMimeCheck when both sides are present.
  if (isImage && mimeType && !mimeType.startsWith('image/')) {
    return {
      isValid: false,
      error: 'MIME type mismatch for image'
    }
  }
  if (isVideo && mimeType && !mimeType.startsWith('video/')) {
    return {
      isValid: false,
      error: 'MIME type mismatch for video'
    }
  }

  const warnings: string[] = []

  if (isImage && hasImageExt && hasImageMime && fileExtension) {
    const expectedTypes = EXTENSION_TO_MIME[fileExtension as AllowedImageExtension]
    if (expectedTypes && !expectedTypes.includes(mimeType as AllowedImageMime)) {
      warnings.push(
        `File extension '${fileExtension}' does not match MIME type '${mimeType}'; this image is re-encoded on save before upload when needed.`
      )
    }
  }

  return warnings.length > 0 ? { isValid: true, warnings } : { isValid: true }
}

/**
 * Validates file size
 */
const validateFileSize = (file: File, options: Required<FileValidationOptions>): FileValidationResult => {
  if (file.size > options.maxSizeBytes) {
    const maxSizeMB = (options.maxSizeBytes / (1024 * 1024)).toFixed(1)

    return {
      isValid: false,
      error: `File size (${(file.size / (1024 * 1024)).toFixed(1)}MB) exceeds maximum allowed size of ${maxSizeMB}MB`
    }
  }

  if (file.size < options.minSizeBytes) {
    const minSizeKB = (options.minSizeBytes / 1024).toFixed(1)

    return {
      isValid: false,
      error: `File size (${(file.size / 1024).toFixed(1)}KB) is below minimum required size of ${minSizeKB}KB`
    }
  }

  return { isValid: true }
}

/**
 * Validates file name for potential security issues
 */
const validateFileName = (file: File): FileValidationResult => {
  const fileName = file.name
  const warnings: string[] = []

  // Check for potentially suspicious file names
  const basename = fileName.includes('.') ? fileName.substring(0, fileName.indexOf('.')) : fileName
  const suspiciousPatterns = [
    /\.{2}/, // Directory traversal
    /[<>:"|?*]/, // Invalid characters
    /^\./, // Hidden files
    /^(con|prn|aux|nul|com[1-9]|lpt[1-9])(\.|$)/i, // Windows reserved names (with optional extension)
    /.{255,}/ // Too long names
  ]

  const reservedPattern = /^(con|prn|aux|nul|com[1-9]|lpt[1-9])(\.|$)/i
  for (const pattern of suspiciousPatterns) {
    if (pattern === reservedPattern) {
      if (reservedPattern.test(fileName) || reservedPattern.test(basename)) {
        return {
          isValid: false,
          error: `File name '${fileName}' is a reserved system name on Windows and is not allowed`
        }
      }
      continue
    }
    if (pattern.test(fileName)) {
      return {
        isValid: false,
        error: `File name '${fileName}' contains invalid or potentially dangerous characters`
      }
    }
  }

  // Check for double extensions (potential disguised files)
  const extensions = fileName.match(/\.[^.]+/g)
  if (extensions && extensions.length > 1) {
    warnings.push(`File has multiple extensions: ${extensions.join('')}. Ensure this is intentional.`)
  }

  return { isValid: true, warnings }
}

/**
 * Main file validation function
 */
export const validateFile = (file: File | null, options: Partial<FileValidationOptions> = {}): FileValidationResult => {
  if (!file) {
    return {
      isValid: false,
      error: 'No file provided'
    }
  }

  const validationOptions = { ...DEFAULT_OPTIONS, ...options }
  const allWarnings: string[] = []

  // Validate file type
  const typeValidation = validateFileType(file, validationOptions)
  if (!typeValidation.isValid) {
    return typeValidation
  }
  if (typeValidation.warnings) {
    allWarnings.push(...typeValidation.warnings)
  }

  // Validate file size
  const sizeValidation = validateFileSize(file, validationOptions)
  if (!sizeValidation.isValid) {
    return sizeValidation
  }
  if (sizeValidation.warnings) {
    allWarnings.push(...sizeValidation.warnings)
  }

  // Validate file name
  const nameValidation = validateFileName(file)
  if (!nameValidation.isValid) {
    return nameValidation
  }
  if (nameValidation.warnings) {
    allWarnings.push(...nameValidation.warnings)
  }

  return {
    isValid: true,
    warnings: allWarnings.length > 0 ? allWarnings : undefined
  }
}

/**
 * Predefined validation configurations for common use cases
 */
export const VALIDATION_PRESETS = {
  RECIPE_IMAGES: {
    allowedTypes: [...ALLOWED_IMAGE_MIMES, AllowedVideoMime.WEBM],
    allowedExtensions: [...ALLOWED_IMAGE_EXTENSIONS, AllowedVideoExtension.WEBM],
    maxSizeBytes: 50 * 1024 * 1024, // 50MB (increased to support videos)
    minSizeBytes: 1024 // 1KB
  },
  RECIPE_VIDEOS: {
    allowedTypes: [AllowedVideoMime.WEBM],
    allowedExtensions: [AllowedVideoExtension.WEBM],
    maxSizeBytes: 50 * 1024 * 1024, // 50MB
    minSizeBytes: 10 * 1024 // 10KB
  },
  RECIPE_PROCEDURE_MEDIA: {
    allowedTypes: [...ALLOWED_IMAGE_MIMES, AllowedVideoMime.WEBM],
    allowedExtensions: [...ALLOWED_IMAGE_EXTENSIONS, AllowedVideoExtension.WEBM],
    maxSizeBytes: 50 * 1024 * 1024, // 50MB (videos can be larger)
    minSizeBytes: 1024 // 1KB
  },

  /** Build Regular/Mini portioning-tab row images — same rules as {@link DEFAULT_OPTIONS} (5MB, images only). */
  PORTIONING_INGREDIENT_IMAGE: { ...DEFAULT_OPTIONS }
}

/** Max size per portioning ingredient image (build form). */
export const PORTIONING_INGREDIENT_IMAGE_MAX_BYTES = VALIDATION_PRESETS.PORTIONING_INGREDIENT_IMAGE.maxSizeBytes

export const PORTIONING_INGREDIENT_IMAGE_HINT = 'JPG, PNG, or WEBP · 1KB–5MB per image'

/** True when file may be a recipe image upload (used by shared `uploadRecipeFile` only). */
export function isRecipeImageUploadCandidate(file: File): boolean {
  return file.type.startsWith('image/') || ALLOWED_IMAGE_NAME_PATTERN.test(file.name)
}

/** `uploadRecipeFile` media fields that browser-re-encode problematic CMS images before POST. */
export const RECIPE_MEDIA_FIELDS_NORMALIZE_BEFORE_UPLOAD = [
  'ingredientPortioning',
  'procedure',
  'recipe',
  'cookStationSetup',
  'prepStationSetup'
] as const

const RECIPE_MEDIA_FIELDS_NORMALIZE_BEFORE_UPLOAD_SET = new Set<string>(RECIPE_MEDIA_FIELDS_NORMALIZE_BEFORE_UPLOAD)

/** True when `uploadRecipeFile` should call {@link normalizeImageFileForUpload} (images only; never webm). */
export function shouldNormalizeRecipeMediaBeforeUpload(mediaField: string, file: File): boolean {
  if (!RECIPE_MEDIA_FIELDS_NORMALIZE_BEFORE_UPLOAD_SET.has(mediaField)) {
    return false
  }

  const lowerName = file.name.toLowerCase()
  if (file.type.startsWith('video/') || lowerName.endsWith(AllowedVideoExtension.WEBM)) {
    return false
  }

  return isRecipeImageUploadCandidate(file)
}

/**
 * Helper function to show user-friendly error messages
 */
export const formatValidationError = (result: FileValidationResult): string => {
  if (result.isValid) {
    return ''
  }

  return result.error || 'File validation failed'
}

/**
 * Helper function to check if file is an image
 */
export const isImageFile = (file: File): boolean => {
  return file.type.startsWith('image/')
}

/**
 * Helper function to check if file is a video
 */
export const isVideoFile = (file: File): boolean => {
  return file.type.startsWith('video/')
}

type DetectedImageFormat = {
  mime: AllowedImageMime
  ext: AllowedImageExtension
}

const IMAGE_FORMAT_FROM_BYTES: ReadonlyArray<{
  mime: AllowedImageMime
  ext: AllowedImageExtension
  matches: (bytes: Uint8Array) => boolean
}> = [
  {
    mime: AllowedImageMime.JPEG,
    ext: AllowedImageExtension.JPG,
    matches: bytes => bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff
  },
  {
    mime: AllowedImageMime.PNG,
    ext: AllowedImageExtension.PNG,
    matches: bytes =>
      bytes.length >= 8 && bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47
  },
  {
    mime: AllowedImageMime.WEBP,
    ext: AllowedImageExtension.WEBP,
    matches: bytes =>
      bytes.length >= 12 &&
      bytes[0] === 0x52 &&
      bytes[1] === 0x49 &&
      bytes[2] === 0x46 &&
      bytes[3] === 0x46 &&
      bytes[8] === 0x57 &&
      bytes[9] === 0x45 &&
      bytes[10] === 0x42 &&
      bytes[11] === 0x50
  }
]

export class RecipeImageUploadPrepareError extends Error {
  constructor(
    message = 'This image could not be prepared for upload. Export it as JPEG from Preview or Photos, then try again.'
  ) {
    super(message)
    this.name = 'RecipeImageUploadPrepareError'
  }
}

/** Sniff magic bytes — extension/MIME from the OS can be wrong (e.g. JPEG saved as .png). */
export function detectImageFormatFromBytes(bytes: Uint8Array): DetectedImageFormat | null {
  for (const format of IMAGE_FORMAT_FROM_BYTES) {
    if (format.matches(bytes)) {
      return { mime: format.mime, ext: format.ext }
    }
  }

  return null
}

function declaredImageExtension(name: string): string | null {
  const dot = name.lastIndexOf('.')
  if (dot <= 0) {
    return null
  }

  return name.slice(dot).toLowerCase()
}

/** True when filename or MIME disagrees with sniffed bytes (e.g. burrito JPEG saved as `.png`). */
export function hasSpoofedImageMetadata(file: File, detected: DetectedImageFormat): boolean {
  const declaredExt = declaredImageExtension(file.name)
  const expectedExt = detected.ext
  const mimeMismatch = file.type !== '' && file.type !== detected.mime
  const extMismatch =
    declaredExt != null &&
    declaredExt !== expectedExt &&
    !(
      (declaredExt === AllowedImageExtension.JPEG || declaredExt === AllowedImageExtension.JPG) &&
      (expectedExt === AllowedImageExtension.JPG || expectedExt === AllowedImageExtension.JPEG)
    )

  return mimeMismatch || extMismatch
}

/** CMS burrito step exports (`Step1-20260514-…-uuid.png`) from the asset pipeline. */
const CMS_EXPORT_IMAGE_FILENAME_PATTERN = /\d{8}-\d{6}-[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i
const CMS_STEP_FILENAME_PATTERN = /^step\s*[12]\b|^step[12]([-.]|$)/i

/** Multipart name for re-encoded uploads — same style as working files (`fresh-salsa.jpg`). */
const PREPARED_RECIPE_IMAGE_BASENAME = 'portioning-image'

function buildPreparedUploadName(detected: DetectedImageFormat): string {
  return `${PREPARED_RECIPE_IMAGE_BASENAME}${detected.ext}`
}

/** True when upload body must be re-encoded in the browser before POST …/media. */
export function needsRecipeImageBodyReencode(
  file: Pick<File, 'name' | 'type'>,
  detected: DetectedImageFormat
): boolean {
  if (hasSpoofedImageMetadata(file as File, detected)) {
    return true
  }

  if (CMS_STEP_FILENAME_PATTERN.test(file.name)) {
    return true
  }

  return detected.mime === AllowedImageMime.JPEG && CMS_EXPORT_IMAGE_FILENAME_PATTERN.test(file.name)
}

function canvasToImageFile(canvas: HTMLCanvasElement, detected: DetectedImageFormat): Promise<File | null> {
  return new Promise(resolve => {
    const onBlob = (blob: Blob | null) => {
      if (!blob || blob.size === 0) {
        resolve(null)

        return
      }
      resolve(
        new File([blob], buildPreparedUploadName(detected), {
          type: detected.mime,
          lastModified: Date.now()
        })
      )
    }

    if (detected.mime === AllowedImageMime.JPEG) {
      canvas.toBlob(onBlob, AllowedImageMime.JPEG, 0.92)
    } else {
      canvas.toBlob(onBlob, detected.mime)
    }
  })
}

async function reencodeViaCreateImageBitmap(file: File, detected: DetectedImageFormat): Promise<File | null> {
  if (typeof createImageBitmap !== 'function') {
    return null
  }

  try {
    const bitmap = await createImageBitmap(file)
    try {
      const canvas = document.createElement('canvas')
      canvas.width = bitmap.width
      canvas.height = bitmap.height
      const ctx = canvas.getContext('2d')
      if (!ctx) {
        return null
      }
      ctx.drawImage(bitmap, 0, 0)

      return canvasToImageFile(canvas, detected)
    } finally {
      bitmap.close()
    }
  } catch {
    return null
  }
}

async function reencodeViaHtmlImage(file: File, detected: DetectedImageFormat): Promise<File | null> {
  if (typeof document === 'undefined') {
    return null
  }

  return new Promise(resolve => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas')
        canvas.width = img.naturalWidth
        canvas.height = img.naturalHeight
        const ctx = canvas.getContext('2d')
        if (!ctx) {
          resolve(null)

          return
        }
        ctx.drawImage(img, 0, 0)
        void canvasToImageFile(canvas, detected).then(resolve)
      } catch {
        resolve(null)
      } finally {
        URL.revokeObjectURL(url)
      }
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      resolve(null)
    }
    img.src = url
  })
}

async function reencodeRecipeImageFile(file: File, detected: DetectedImageFormat): Promise<File | null> {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return null
  }

  const fromBitmap = await reencodeViaCreateImageBitmap(file, detected)
  if (fromBitmap) {
    return fromBitmap
  }

  return reencodeViaHtmlImage(file, detected)
}

function isProblematicRecipeExportImage(file: Pick<File, 'name'>): boolean {
  return CMS_STEP_FILENAME_PATTERN.test(file.name) || CMS_EXPORT_IMAGE_FILENAME_PATTERN.test(file.name)
}

/**
 * Prepare recipe image uploads before `POST …/media`.
 * Normal files pass through unchanged. Step1/Step2 CMS exports and JPEG-as-PNG are
 * re-encoded in the browser so test WAF sees the same kind of upload as `fresh-salsa.jpg`.
 */
export async function normalizeImageFileForUpload(file: File): Promise<File> {
  if (!isRecipeImageUploadCandidate(file)) {
    return file
  }

  let detected: DetectedImageFormat | null = null
  try {
    const head = new Uint8Array(await file.slice(0, 12).arrayBuffer())
    detected = detectImageFormatFromBytes(head)
    if (!detected) {
      return file
    }

    if (!needsRecipeImageBodyReencode(file, detected)) {
      return file
    }

    const reencoded = await reencodeRecipeImageFile(file, detected)
    if (reencoded) {
      return reencoded
    }

    throw new RecipeImageUploadPrepareError()
  } catch (error) {
    if (error instanceof RecipeImageUploadPrepareError) {
      throw error
    }

    if ((detected && needsRecipeImageBodyReencode(file, detected)) || isProblematicRecipeExportImage(file)) {
      throw new RecipeImageUploadPrepareError()
    }

    return file
  }
}
